/**
 * On-screen number pad.
 *
 * A till often has no keyboard, or has one the cashier cannot reach past the
 * customer. Every place a number is entered - a tendered amount, a discount, a
 * PIN, a counted drawer - offers this.
 *
 * Values are built as strings rather than numbers so a half-typed "1200." is
 * representable. Converting on every keystroke would make the decimal point
 * disappear as it is typed.
 */

import { FiDelete } from 'react-icons/fi';

const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'back'];

const NumericKeypad = ({
    value = '',
    onChange,
    onEnter = null,
    maxDecimals = 2,
    // Round-number shortcuts, the notes a customer actually hands over.
    quickAmounts = null,
    disabled = false,
    className = ''
}) => {
    const press = (key) => {
        if (disabled) return;

        if (key === 'back') {
            onChange(String(value).slice(0, -1));
            return;
        }

        const current = String(value);

        if (key === '.') {
            if (maxDecimals === 0 || current.includes('.')) return;
            onChange(current === '' ? '0.' : `${current}.`);
            return;
        }

        // Stop a third decimal place being typed into a money field.
        if (current.includes('.')) {
            const [, decimals = ''] = current.split('.');
            if (decimals.length >= maxDecimals) return;
        }

        // A leading zero is only meaningful before a decimal point.
        const next = current === '0' ? key : current + key;
        onChange(next);
    };

    return (
        <div className={className}>
            {quickAmounts?.length > 0 && (
                <div className="mb-2 grid grid-cols-4 gap-1.5">
                    {quickAmounts.map((amount) => (
                        <button
                            key={amount}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(String(amount))}
                            className="btn-secondary btn-sm tabular !px-1"
                        >
                            {amount.toLocaleString('en-PK')}
                        </button>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-3 gap-1.5">
                {KEYS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        disabled={disabled}
                        onClick={() => press(key)}
                        className={`btn min-h-[3rem] text-lg font-semibold
                                    ${key === 'back'
                                        ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                                        : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 active:bg-slate-100'}`}
                    >
                        {key === 'back' ? <FiDelete className="h-5 w-5" /> : key}
                    </button>
                ))}
            </div>

            {onEnter && (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onEnter}
                    className="btn-primary mt-1.5 w-full min-h-[3rem] text-base"
                >
                    Enter
                </button>
            )}
        </div>
    );
};

export default NumericKeypad;
